import { useMediaQuery } from '@mantine/hooks'
import Meta from '../common/Meta'
import Header from '../common/components/header/Header'
import StatCard from '../stories/StatCard/StatCard'
import clsx from 'clsx'
import Tabs from '../stories/Tabs/Tabs'
import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { IconChevronRight, IconNews } from '@tabler/icons-react'
import { usePlayerRanks, useTeamRanks } from '../ranks/api'
import { useProgress } from './api'
import CompetitionDisplay from '../navigation/CompetitionDisplay'
import { useActiveCompetitions } from '../navigation/api'
import classes from './Home.module.css'
import { useNews } from '../news/api'
import NewsCard from '../news/NewsCard'
import { useIsPlayer } from '../common/api'
import { Typography } from '../common/typography'
import { AspectRatio, Loader, ScrollArea, Image } from '@mantine/core'
import {
  useMarkAsRead,
  useNotificationsInfinite,
  Notification,
} from '../notifications/api'
import RowData from '../common/components/row/RowData'
import Announcement from './Announcement'

export default function Home() {
  const navigate = useNavigate()
  const location = useLocation()
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const tabs = [
    {
      value: 'teams',
      label: 'Top Teams',
    },
    {
      value: 'players',
      label: 'Top Players',
    },
  ]
  const [activeTab, setActiveTab] = useState<string>('teams')
  const { player_id, competition_id } = useIsPlayer()

  const { mutate: markAsRead } = useMarkAsRead()

  const {
    data: notificationsInfinite,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotificationsInfinite()

  const notifications =
    notificationsInfinite?.pages
      .flatMap((page) => page.data)
      .filter((notification) => {
        if (notification.entity_type === 'news') {
          return notification.metadata.news_id !== undefined
        }
        return true
      }) ?? []

  function handleNotificationSelect(notification: Notification) {
    markAsRead({ notification_id: notification.notification_id })
    close()
    if (notification.entity_type === 'news') {
      navigate(`/news/${notification.metadata.news_id}`)
    } else if (notification.entity_type === 'post') {
      navigate(
        `/competitions/${notification.metadata.competition_id}/arena#post-${notification.metadata.post_id}`
      )
    } else if (notification.entity_type === 'comment') {
      navigate(
        `/competitions/${notification.metadata.competition_id}/arena#post-${notification.metadata.post_id}-comment-${notification.metadata.comment_id}`
      )
    }
  }

  const { data } = useActiveCompetitions()
  const active_competitions = data?.competitions || []

  const { data: progress } = useProgress({
    competition_id: competition_id,
    player_id: player_id,
  })

  const { data: topTeamsResponse } = useTeamRanks({
    competition_id: competition_id,
    page: 1,
    page_size: 3,
  })

  const { data: topPlayersResponse } = usePlayerRanks({
    competition_id: competition_id,
    page: 1,
    page_size: 3,
  })

  const topTeams = topTeamsResponse.items
  const topPlayers = topPlayersResponse.items

  const { data: news } = useNews({
    page: 1,
    page_size: 3,
    competition_id: competition_id,
  })

  const { data: announcements } = useNews({
    page: 1,
    page_size: 1,
    competition_id: competition_id,
    is_announcement: true,
  })

  const teamsData = useMemo(() => {
    return topTeams.map((team) => ({
      individual: '',
      individual_image: '',
      team: team.team_name,
      team_id: team.team_id,
      team_logo: team.team_logo.thumbnail,
      score: team.rating,
      rank: team.rank,
    }))
  }, [topTeams])

  const playersData = useMemo(() => {
    return topPlayers.map((player) => ({
      individual: player.player_name,
      individual_id: player.player_id,
      individual_image: player.player_photo.main,
      team: player.team_name || '',
      team_logo: player.team_logo.mini,
      score: player.rating,
      rank: player.rank,
    }))
  }, [topPlayers])

  return (
    <div className={classes.container}>
      <Meta title="Home" />
      <Header title="Home" />
      {!isDesktop && (
        <div className={classes.competitionDisplay}>
          <CompetitionDisplay
            name={
              active_competitions.find(
                (competition) => competition.competition_id === competition_id
              )?.name || ''
            }
            logo={
              active_competitions.find(
                (competition) => competition.competition_id === competition_id
              )?.logo.thumbnail || ''
            }
          />
        </div>
      )}
      <div
        className={clsx(classes.content, {
          [classes.contentMobile]: !isDesktop,
        })}
      >
        <div
          className={clsx(classes.containerWrapper, {
            [classes.containerWrapperMobile]: !isDesktop,
          })}
        >
          {announcements.news.length > 0 &&
            announcements.news
              .slice(0, 1)
              .map((announcement) => (
                <Announcement announcement={announcement} />
              ))}
          <div
            className={clsx(classes.wrapper, {
              [classes.withoutProgress]: !player_id,
              [classes.wrapperMobile]: !isDesktop,
            })}
          >
            {progress && (
              <div className={classes.cards}>
                <StatCard
                  image={progress.user_photo ? progress.user_photo.main : ''}
                  name={progress.user_name}
                  id={progress.player_id}
                  rank={progress.current_round_rank}
                  score={progress.current_round_rating.toFixed(2)}
                  competition_id={competition_id}
                  title="My Progress"
                  link_title="See More"
                />
                {progress.team_id && (
                  <StatCard
                    image={
                      progress.team_logo ? progress.team_logo.thumbnail : ''
                    }
                    name={progress.team_name || ''}
                    id={progress.team_id}
                    rank={progress.team_current_rank}
                    score={progress.team_current_rating?.toFixed(2) ?? null}
                    competition_id={competition_id}
                    title="My Team"
                    link_title="All Rankings"
                    isPlayer={false}
                  />
                )}
              </div>
            )}
            <div className={classes.dataWrapper}>
              <Tabs items={tabs} value={activeTab} onChange={setActiveTab} />
              {activeTab === 'teams' ? (
                <div className={classes.data}>
                  {teamsData.map((team) => (
                    <Link
                      className={classes.link}
                      key={team.team_id}
                      to={`/competitions/${competition_id}/teams/${team.team_id}/overview`}
                      state={{ from: location }}
                    >
                      <RowData
                        team_logo={team.team_logo}
                        team_name={team.team}
                        score={team.score}
                        rank={team.rank}
                      />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className={classes.data}>
                  {playersData.map((player) => (
                    <Link
                      className={classes.link}
                      key={player.individual_id}
                      to={`/competitions/${competition_id}/players/${player.individual_id}`}
                      state={{ from: location }}
                    >
                      <RowData
                        team_logo={player.team_logo}
                        team_name={player.team}
                        score={player.score}
                        rank={player.rank}
                        player_name={player.individual}
                        player_image={player.individual_image}
                      />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className={classes.newsWrapper}>
            <div className={classes.newsHeader}>
              <Typography
                level={2}
                fs="xl"
                fw={500}
                lh="125"
                fc={{ light: 'black', dark: 'gray-2' }}
              >
                Latest News
              </Typography>
              <Link to="/news" className={classes.link}>
                All News
                <IconChevronRight className={classes.linkIcon} />
              </Link>
            </div>
            <div className={classes.news}>
              {news.news.map((item) => (
                <NewsCard
                  key={item.news_id}
                  news={item}
                  className={!isDesktop ? undefined : classes.homeNewsCard}
                />
              ))}
            </div>
          </div>
        </div>
        {isDesktop && (
          <div className={classes.notifications}>
            <div className={classes.notificationsHeader}>
              <Typography fs="lg" fw={500} lh="125">
                Notifications
              </Typography>
            </div>
            <ScrollArea.Autosize mah={1000}>
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <div
                    key={notification.notification_id}
                    className={classes.notificationRow}
                    data-read={!notification.is_read}
                    onClick={() => handleNotificationSelect(notification)}
                  >
                    {notification.entity_type === 'post' ||
                    notification.entity_type === 'comment' ? (
                      <AspectRatio ratio={1 / 1}>
                        <Image
                          src={notification.metadata.author_photo.thumbnail}
                          alt={notification.metadata.author_name}
                          className={classes.authorImage}
                          fit="scale-down"
                        />
                      </AspectRatio>
                    ) : (
                      <div className={classes.newsContainer}>
                        <IconNews className={classes.newsIcon} />
                      </div>
                    )}
                    <div className={classes.notificationContainer}>
                      <Typography lh="140">{notification.content}</Typography>
                    </div>
                    <IconChevronRight
                      className={classes.notificationIcon}
                      data-read={!notification.is_read}
                    />
                  </div>
                ))
              ) : (
                <Typography ta="center">No new notifications.</Typography>
              )}
            </ScrollArea.Autosize>
            <div
              className={clsx(classes.notificationsFooter, {
                [classes.notificationsFooterEmpty]: !hasNextPage,
              })}
            >
              {hasNextPage && (
                <div
                  className={classes.loaderContainer}
                  onClick={() => fetchNextPage()}
                >
                  {isFetchingNextPage ? (
                    <Loader color="#0B6B7B" />
                  ) : (
                    <Typography
                      fs="sm"
                      lh="150"
                      fc={{ light: 'primary-5', dark: 'primary-3' }}
                      underline
                    >
                      Load More
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
